import { Bot } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router";

import { useProjectOverviewQuery } from "@/api/hooks";
import { usePreviewCandidatesQuery } from "@/api/preview";
import {
  SIM_POLL_INTERVAL_MS,
  classifySimRunFailure,
  isSimRunTerminal,
  useCreatePreviewSimRunMutation,
  usePreviewSimPersonasQuery,
  usePreviewSimRunQuery,
  type SimRunFailure,
} from "@/api/previewSim";
import { useCapability } from "@/auth/SessionContext";
import { isCurrent } from "@/lib/candidateFreshness";
import { previewRunPath } from "@/lib/previewInsights";
import {
  SIM_DEFAULT_TURNS,
  SIM_LANGUAGES,
  SIM_LANGUAGE_LABELS,
  SIM_MAX_TURNS,
  SIM_MIN_TURNS,
  SIM_STATUS_LABELS,
  describeEndReason,
  personasFor,
  readMaxTurns,
  type SimLanguage,
} from "@/lib/previewSim";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { Field, Input, Select } from "@/ui/Input";

/**
 * The refusals, in a clinician's words. `satisfies` so a new kind fails the
 * build rather than rendering blank.
 */
const FAILURE_MESSAGES = {
  stale_target:
    "This project's preview target changed since you opened this page. Reload, then run again.",
  duplicate_command:
    "That run request was already used for a different run. Run again to start a new one.",
  admission_refused:
    "The preview target refused this testing version's safety posture, so no run was started.",
  candidate_unusable:
    "This testing version is no longer available or has expired. Freeze a new one from the Preview dialog.",
  language_unavailable:
    "This preview target does not offer this language for a conversation, so a run cannot be set up. Choose another language.",
  candidate_validator_mismatch:
    "This testing version was validated by another engine build and must be rebuilt before it can run.",
  preview_target:
    "This project's preview target is not available for previews right now.",
  denied: "Your account cannot start simulated-patient runs here.",
  unknown_dispatch:
    "The service did not confirm whether the run started. Run again to retry: the same request cannot start a second run.",
  error: "The run could not be started.",
} as const satisfies Record<SimRunFailure["kind"], string>;

/**
 * Run a simulated patient against the Project's current testing version
 * (Preview history page).
 *
 * The browser names a persona, a language and a turn budget — never a patient,
 * a target or an endpoint. The candidate is the newest one the SERVER says is
 * current; an older or expired one is never offered. A run spends model calls,
 * so the request carries one idempotency key per attempt: a retry after a lost
 * answer reuses it and cannot start a second run, and a changed form mints a
 * new one.
 *
 * After the dispatch the panel follows the run's own status (body-free) until
 * it ends, then links to Preview Insights, where the conversation is read.
 */
export function SimulatedPatientPanel({ projectId }: { projectId: string }) {
  const create = useCapability("preview.create");
  // Disabled with its reason, never absent (ADR 0001): and it reads nothing
  // until the caller may actually run one.
  return create.allowed ? (
    <SimulatedPatientForm projectId={projectId} />
  ) : (
    <Card>
      <CardHeader>
        <CardTitle>Run a simulated patient</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-sm text-muted-foreground">
          {create.reason} Running a simulated patient needs the
          &quot;preview.create&quot; permission.
        </p>
      </CardBody>
    </Card>
  );
}

function SimulatedPatientForm({ projectId }: { projectId: string }) {
  const create = useCapability("preview.create");
  const inspect = useCapability("preview.inspect");
  const overview = useProjectOverviewQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);
  const personas = usePreviewSimPersonasQuery(projectId);
  const mutation = useCreatePreviewSimRunMutation(projectId);

  const [language, setLanguage] = useState<SimLanguage>("he");
  const [personaId, setPersonaId] = useState("");
  const [turnsText, setTurnsText] = useState(String(SIM_DEFAULT_TURNS));
  const idempotencyKey = useRef<string>(crypto.randomUUID());
  const [runRef, setRunRef] = useState<string | null>(null);

  const choices = personasFor(personas.data?.personas ?? [], language);
  const selectedPersona = choices.find((choice) => choice.id === personaId);
  const maxTurns = readMaxTurns(turnsText);
  const newest = candidates.data?.items[0] ?? null;
  const candidate =
    newest !== null && isCurrent(newest) && newest.validity?.state !== "expired"
      ? newest
      : null;
  const revision = overview.data?.project.previewTargetRevision;
  const status = usePreviewSimRunQuery(projectId, runRef ?? "", {
    enabled: runRef !== null,
    pollIntervalMs: SIM_POLL_INTERVAL_MS,
  });
  const failure = mutation.isError
    ? classifySimRunFailure(mutation.error)
    : null;

  const blocker =
    candidate === null
      ? candidates.isPending
        ? "Loading the testing version…"
        : "There is no current testing version. Freeze one from the Preview dialog first."
      : revision === undefined
        ? "Loading the project…"
        : selectedPersona === undefined
          ? "Choose a persona."
          : maxTurns === null
            ? `Enter a turn budget from ${SIM_MIN_TURNS} to ${SIM_MAX_TURNS}.`
            : null;

  /** A changed form is a different run: it gets its own key, and an old refusal stops applying. */
  function changed() {
    idempotencyKey.current = crypto.randomUUID();
    mutation.reset();
  }

  function run() {
    if (
      blocker !== null ||
      candidate === null ||
      revision === undefined ||
      maxTurns === null
    ) {
      return;
    }
    mutation.mutate(
      {
        candidateId: candidate.candidateId,
        personaId,
        language,
        maxTurns,
        expectedPreviewTargetRevision: revision,
        idempotencyKey: idempotencyKey.current,
      },
      {
        onSuccess(created) {
          setRunRef(created.previewRunRef);
          // This run is done being asked for: pressing Run again means ANOTHER
          // run. Only an unconfirmed dispatch keeps its key, so its retry
          // cannot start a second one.
          idempotencyKey.current = crypto.randomUUID();
        },
      },
    );
  }

  const live = status.data ?? null;
  const ended = live !== null && isSimRunTerminal(live);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="flex items-center gap-2">
            <Bot aria-hidden="true" className="h-4 w-4" />
            Run a simulated patient
          </span>
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="max-w-3xl text-sm text-muted-foreground">
          A built-in patient simulator talks to this project&apos;s current
          testing version in a synthetic conversation. No preview link is
          opened: follow the run here, then read the conversation in Preview
          Insights.
        </p>

        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Language">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={language}
                onChange={(event) => {
                  setLanguage(event.target.value as SimLanguage);
                  setPersonaId("");
                  changed();
                }}
              >
                {SIM_LANGUAGES.map((code) => (
                  <option key={code} value={code}>
                    {SIM_LANGUAGE_LABELS[code]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Simulated patient">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={personaId}
                aria-disabled={choices.length === 0 || undefined}
                onChange={(event) => {
                  setPersonaId(event.target.value);
                  changed();
                }}
              >
                <option value="">
                  {personas.isPending && create.allowed
                    ? "Loading…"
                    : choices.length === 0
                      ? "None offered in this language"
                      : "Choose a persona"}
                </option>
                {choices.map((choice) => (
                  <option key={choice.id} value={choice.id}>
                    {choice.title}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Turn budget">
            {({ inputId }) => (
              <Input
                id={inputId}
                inputMode="numeric"
                value={turnsText}
                onChange={(event) => {
                  setTurnsText(event.target.value);
                  changed();
                }}
                aria-invalid={maxTurns === null ? true : undefined}
              />
            )}
          </Field>
        </div>

        {selectedPersona ? (
          <p dir="auto" className="max-w-3xl text-sm text-muted-foreground">
            {selectedPersona.description}
          </p>
        ) : null}
        {personas.isError ? (
          <p role="alert" className="text-sm text-destructive">
            The persona list could not be loaded from the preview target.
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={blocker !== null || mutation.isPending}
            title={blocker ?? undefined}
            onClick={run}
          >
            {mutation.isPending
              ? "Starting…"
              : failure?.kind === "unknown_dispatch"
                ? "Retry run"
                : "Run simulated patient"}
          </Button>
          {blocker !== null ? (
            <span className="text-sm text-muted-foreground">{blocker}</span>
          ) : null}
        </div>

        {failure !== null ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {FAILURE_MESSAGES[failure.kind]}
          </p>
        ) : null}

        {runRef !== null ? (
          <div
            role="status"
            className="space-y-2 rounded-md border border-border bg-muted/40 p-3 text-sm"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">Run started.</span>
              {live ? (
                <Badge
                  tone={live.status === "failed" ? "destructive" : "outline"}
                >
                  {SIM_STATUS_LABELS[live.status]}
                </Badge>
              ) : null}
              {live ? (
                <span>
                  Turn <span dir="ltr">{live.turnsCompleted}</span> of{" "}
                  <span dir="ltr">{live.maxTurns}</span>
                </span>
              ) : status.data === null ? (
                <span>Waiting for the preview target to report the run…</span>
              ) : null}
            </div>
            {ended ? (
              <p>
                {describeEndReason(live.endReason)}
                {live.error ? ` — ${live.error}` : ""}
              </p>
            ) : null}
            {status.isError ? (
              <p className="text-muted-foreground">
                The run&apos;s status could not be read just now. It continues
                on the preview target.
              </p>
            ) : null}
            {inspect.allowed ? (
              <Button asChild size="sm">
                <Link to={previewRunPath(projectId, runRef)}>
                  Open Preview Insights
                </Link>
              </Button>
            ) : (
              <p className="text-muted-foreground">
                {inspect.reason} Viewing the conversation needs the
                &quot;preview.inspect&quot; permission.
              </p>
            )}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
